/* eslint-disable max-classes-per-file */
import React, { Component, PureComponent } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import lodash from 'lodash'
import { View, ActivityIndicator, StyleSheet } from 'react-native'
import { bindingTypes, listTypes } from '@adalo/constants'

import PaginatedFetchComponent, {
  connectPaginatedFetch,
} from 'components/Table/PaginatedFetchComponent'
import PaginationControls from 'components/Table/PaginationControls'

import { getNode, getNodeY, getNodeHeight } from 'ducks/pushTreeMap'

import { sortItems, listsEqual } from 'utils/lists'
import { getRowHeight } from 'utils/variableHeight/getHeights'
import { getDeviceType } from 'utils/device'

import ActionWrapper from '../ActionWrapper'
import ListWrapper from './ListWrapper'

const getColumnCount = object => {
  if (object.attributes.listType === listTypes.GRID) {
    return object.attributes.columnCount ?? 1
  }

  return 1
}

const getRowMargin = object => {
  return object.attributes.rowMargin ?? 0
}

const getAttribute = (object, key, deviceType) => {
  return object[deviceType]?.attributes?.[key] || object.attributes?.[key]
}

export class UnconnectedListItem extends PureComponent {
  static contextTypes = {
    getBinding: PropTypes.func,
    getBindings: PropTypes.func,
    getBindingsList: PropTypes.func,
    getDatasources: PropTypes.func,
  }

  static childContextTypes = {
    getBinding: PropTypes.func,
    getBindings: PropTypes.func,
    getBindingsList: PropTypes.func,
  }

  getChildContext() {
    return {
      getBinding: this.getBinding,
      getBindings: this.getBindings,
      getBindingsList: this.getBindingsList,
    }
  }

  getBinding = objectId => {
    const { item, listId, object } = this.props
    const { getBinding } = this.context

    if (
      objectId === listId ||
      objectId === (object.dataBinding && object.dataBinding.id)
    ) {
      try {
        return {
          ...item,
          id: JSON.parse(item.id),
        }
      } catch {
        // do nothing
      }

      return item
    } else if (getBinding) {
      return getBinding(objectId)
    }
  }

  getBindings = () => {
    const { item, datasourceId, tableId } = this.props
    const { getBindings } = this.context

    let result = {}

    if (getBindings) {
      result = getBindings()
    }

    if (item) {
      const key = `${datasourceId}.${tableId}`
      result[key] = item.id
    }

    return result
  }

  getBindingsList = () => {
    const { getBindingsList } = this.context
    const { item } = this.props

    const bindingsList = getBindingsList()

    if (item) return bindingsList.concat([item.id])
    return bindingsList
  }

  render() {
    const {
      children,
      rowMargin,
      object,
      component,
      marginLeft,
      marginRight,
      newHeight,
      isResponsiveComponent,
      deviceType,
      listItemBottom,
      rowHeight,
      getNodeYFromId,
    } = this.props

    const styles = { marginBottom: rowMargin }

    if (isResponsiveComponent) {
      if (Math.max(rowHeight, newHeight) === 0) {
        styles.opacity = 0
      }

      styles.height = newHeight
    }

    let adjustedChildren = [...children]
    if (object?.attributes?.masonry === false) {
      adjustedChildren = children.map(child => {
        const childY = getAttribute(child.props.object, 'y', deviceType)
        const childHeight = getAttribute(
          child.props.object,
          'height',
          deviceType
        )

        if (listItemBottom === childY + childHeight) {
          if (isResponsiveComponent) {
            const nodeY = getNodeYFromId(child.props.pushId)

            const childAdjustedY = getAttribute(
              child.props.object,
              'adjustedY',
              deviceType
            )
            const newY = nodeY || childAdjustedY

            return {
              ...child,
              props: {
                ...child.props,
                adjustedNodeHeight: rowHeight - newY,
              },
            }
          }

          return {
            ...child,
            layout: {
              ...child.layout,
              flex: 1,
            },
          }
        }
        return child
      })

      if (!isResponsiveComponent) {
        styles.flex = 1
        styles['align-items'] = 'stretch'
      }
    }

    if (object.actions && Object.keys(object.actions).length > 0) {
      return (
        <ActionWrapper
          style={styles}
          bindingData={{}}
          object={object}
          component={component}
          hitSlop={0}
        >
          {adjustedChildren}
        </ActionWrapper>
      )
    }

    const marginSettings = { marginLeft, marginRight }

    return (
      <View style={[marginSettings, styles]} pointerEvents="box-none">
        {adjustedChildren}
      </View>
    )
  }
}

const mapStateToProps = (
  state,
  {
    component,
    listItemPushId,
    isResponsiveComponent,
    object,
    item,
    pushId,
    app,
  }
) => {
  const { attributes } = object || {}
  const { masonry } = attributes || {}

  const deviceType = getDeviceType(undefined, app)
  let listItemBottom = 0
  let rowHeight = 0
  const newHeight = isResponsiveComponent
    ? getNodeHeight(state, component.id, listItemPushId)
    : 0

  if (isResponsiveComponent && masonry === false) {
    const list = getNode(state, component.id, pushId)

    if (list) {
      listItemBottom = object.children.reduce((max, child) => {
        const childY = getAttribute(child, 'y', deviceType)
        const childHeight = getAttribute(child, 'height', deviceType)

        return Math.max(max, childY + childHeight)
      }, 0)

      rowHeight = getRowHeight(list, object, item)
    }
  }

  const getNodeYFromId = pushId => getNodeY(state, component.id, pushId)

  return {
    newHeight,
    deviceType,
    listItemBottom,
    rowHeight,
    getNodeYFromId,
  }
}

export const ListItem = connect(mapStateToProps)(UnconnectedListItem)

export class WrappedListItem extends Component {
  render() {
    const {
      object,
      bindingData,
      component,
      renderChildren,
      isResponsiveComponent,
      pushId,
      app,
      getFlags,
      parentLayoutMetadata,
    } = this.props

    const { rowMargin } = object.attributes
    const { marginLeft, marginRight } = object.layout

    const item = bindingData[0]
    const { datasourceId, tableId } = (item && item._meta) || {}

    if (!object.children?.[0]) {
      return null
    }

    let prototype = [object.children[0]]
    const listItemPushId = `${pushId} - ${item.id}`
    if (isResponsiveComponent) {
      prototype = object.children
    }

    return (
      <ListItem
        listItemPushId={listItemPushId}
        pushId={pushId}
        listId={object.id}
        item={item}
        rowMargin={rowMargin}
        datasourceId={datasourceId}
        tableId={tableId}
        object={object}
        component={component}
        marginLeft={marginLeft}
        marginRight={marginRight}
        isResponsiveComponent={isResponsiveComponent}
        app={app}
        getFlags={getFlags}
      >
        {renderChildren(prototype, { [object.id]: item }, parentLayoutMetadata)}
      </ListItem>
    )
  }
}

export class List extends PaginatedFetchComponent {
  static contextTypes = {
    getBindings: PropTypes.func,
    getStore: PropTypes.func,
    getParams: PropTypes.func,
    getBaseURL: PropTypes.func,
    getApp: PropTypes.func,
    getDatasources: PropTypes.func,

    setListItems: PropTypes.func,
  }

  getListKey = () => {
    const { getBindings } = this.context
    const { object } = this.props

    // TODO: Figure out a better way to avoid virtualizedlist listKey errors
    if (!this.keySuffix) {
      this.keySuffix = Math.round(Math.random() * 100000)
    }

    const { keySuffix } = this

    const ids = Object.values(getBindings())

    return `list-${object.id}-${keySuffix}-${ids.join('.')}`
  }

  getCurrentPage() {
    return this.state.currentPage
  }

  getCurrentPageItems(listItemIds = []) {
    if (!this.isManuallyPaginated()) {
      return listItemIds
    }
    const { currentPage } = this.state
    const pageSize = this.getPageSize()

    const pageItems = listItemIds.slice(
      currentPage * pageSize,
      (currentPage + 1) * pageSize
    )

    return pageItems
  }

  handlePageChange = () => {
    const { pushId, object, bindingData } = this.props
    const { setListItems } = this.context
    const listItemIds = bindingData?.map(({ id }) => id) || []

    setListItems(pushId, object, this.getCurrentPageItems(listItemIds))
  }

  handleNextPage = () => {
    this.setState(
      ({ currentPage }) => ({
        currentPage: currentPage + 1,
      }),
      () => {
        this.fetchCurrentPageData()
        this.handlePageChange()
      }
    )
  }

  handlePrevPage = () => {
    this.setState(
      ({ currentPage }) => ({ currentPage: Math.max(0, currentPage - 1) }),
      () => {
        this.fetchCurrentPageData()
        this.handlePageChange()
      }
    )
  }

  renderItem = ({ item, index }) => {
    const {
      object,
      component,
      renderChildren,
      isResponsiveComponent,
      pushId,
      app,
      getFlags,
      parentLayoutMetadata,
    } = this.props
    const { dataBinding, attributes } = object
    const { rowMargin, masonry } = attributes

    const { datasourceId, tableId } = dataBinding.source

    if (!object.children?.[0]) {
      return null
    }

    let prototype = [object.children[0]]
    const listItemPushId = `${pushId} - ${item.id}`

    if (isResponsiveComponent) {
      prototype = object.children.map(child => ({
        ...child,
        attributes: { ...child.attributes },
        layout: { ...child.layout },
      }))
    } else if (!masonry) {
      prototype = [
        {
          ...object.children[0],
          layout: {
            ...object.children[0].layout,
            flex: 1,
            'align-items': 'stretch',
            'align-self': 'stretch',
          },
          children: object.children[0].children.map(child => {
            if (child.children && child.children.length > 0) {
              return {
                ...child,
                layout: {
                  ...child.layout,
                  flex: 1,
                  'align-self': 'stretch',
                },
              }
            }

            return child
          }),
        },
      ]
    }

    return (
      <ListItem
        listItemPushId={listItemPushId}
        pushId={pushId}
        listId={object.id}
        item={item}
        rowMargin={rowMargin}
        datasourceId={datasourceId}
        tableId={tableId}
        object={object}
        component={component}
        isResponsiveComponent={isResponsiveComponent}
        app={app}
        getFlags={getFlags}
      >
        {renderChildren(
          prototype,
          { [object.id]: item },
          { ...parentLayoutMetadata, index }
        )}
      </ListItem>
    )
  }

  shouldComponentUpdate(newProps) {
    const {
      pushId,
      object,
      bindingData,
      newY,
      isResponsiveComponent,
      parentLayoutMetadata,
    } = this.props

    if (!lodash.isEqual(parentLayoutMetadata, newProps.parentLayoutMetadata)) {
      return true
    }

    const { dataBinding } = object
    const { setListItems } = this.context

    if (!dataBinding || dataBinding.bindingType !== bindingTypes.LIST) {
      return true
    }

    if (isResponsiveComponent && newY !== newProps.newY) {
      return true
    }

    if (getColumnCount(object) !== getColumnCount(newProps.object)) {
      return true
    }

    if (getRowMargin(object) !== getRowMargin(newProps.object)) {
      return true
    }

    const listItemIds = bindingData?.map(({ id }) => id) || []
    const newListItemIds = newProps.bindingData?.map(({ id }) => id) || []

    if (
      isResponsiveComponent &&
      !listsEqual(bindingData, newProps.bindingData) &&
      !lodash.isEqual(listItemIds, newListItemIds)
    ) {
      setListItems(pushId, object, this.getCurrentPageItems(newListItemIds))
    }

    return !listsEqual(bindingData, newProps.bindingData)
  }

  componentDidMount() {
    const { pushId, object, bindingData, isResponsiveComponent } = this.props
    const { setListItems } = this.context

    if (isResponsiveComponent) {
      const listItemPushIds = bindingData?.map(({ id }) => id) || []
      setListItems(pushId, object, this.getCurrentPageItems(listItemPushIds))
    }
  }

  render() {
    const { object, bindingData, isResponsiveComponent } = this.props
    const { getStore, getParams } = this.context
    const { layout } = object
    const { currentPage } = this.state
    const rowMargin = getRowMargin(object)

    if (
      object.dataBinding &&
      object.dataBinding.bindingType === bindingTypes.LIST &&
      !Array.isArray(bindingData)
    ) {
      return (
        <View style={styles.loader}>
          <ActivityIndicator color="#999999" />
        </View>
      )
    }

    const opts = { state: getStore().getState(), getParams }
    const listItems = sortItems(bindingData, object.dataBinding, opts)

    const listKey = this.getListKey()
    const numberOfColumns = getColumnCount(object)
    const pageSize = this.getPageSize()
    const hasNextPage = (currentPage + 1) * pageSize < listItems.length
    const showPagination = this.isManuallyPaginated()

    const marginBottom = listItems.length > 0 && rowMargin ? -1 * rowMargin : 0

    const wrapperStyles = {
      ...layout,
      marginBottom,
    }

    const listItemsToRender = showPagination
      ? listItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
      : listItems

    return (
      <View style={wrapperStyles} pointerEvents="box-none">
        <ListWrapper
          listKey={listKey}
          data={listItemsToRender}
          renderItem={this.renderItem}
          numberOfColumns={numberOfColumns}
          spacing={rowMargin}
          object={object}
          isResponsiveComponent={isResponsiveComponent}
          pageSize={showPagination ? pageSize : undefined}
        />
        {showPagination && listItems.length > pageSize && (
          <PaginationControls
            currentPage={currentPage}
            nextDisabled={!hasNextPage}
            goToNextPage={this.handleNextPage}
            goToPreviousPage={this.handlePrevPage}
            theme={
              object.theme || {
                cellText: { color: '#000000' },
              }
            }
            showPageNumber
            totalPages={Math.ceil(listItems.length / pageSize)}
          />
        )}
      </View>
    )
  }
}

const styles = StyleSheet.create({
  input: {
    position: 'absolute',
  },
  loader: {
    paddingTop: 10,
    paddingBottom: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
})

export default connectPaginatedFetch(List)
